import { Globe, MapPin } from 'lucide-react';
import { useEffect, useId, useMemo, useRef, useState, type InputHTMLAttributes, type KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { searchPlaces, type FoundPlace } from '../../io/nominatim';
import { matchPlaces, treePlaces, withLocations } from '../../model/places';
import { commit, getState, useApp } from '../store';
import { useDropdownPlace } from './common';

type Item = { kind: 'tree'; label: string } | { kind: 'search' } | ({ kind: 'found' } & FoundPlace);

type Lookup = { q: string; status: 'loading' | 'error' } | { q: string; status: 'done'; found: FoundPlace[] };

/**
 * A place field that suggests places already in the tree as you type, and, when asked (the
 * last item), searches OpenStreetMap. Nothing is sent anywhere until that item is chosen. A place
 * picked from OpenStreetMap also saves where it is, for the Map view.
 * Enter keeps what was typed unless a suggestion is highlighted with the arrow keys.
 */
export function PlaceInput({
  value,
  onChange,
  onPick,
  onKeyDown,
  onBlur,
  ...rest
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'> & {
  value: string;
  onChange: (value: string) => void;
  /** A suggestion was chosen (after onChange); the People table saves the cell then. */
  onPick?: (value: string) => void;
}) {
  const tree = useApp((s) => s.tree);
  const canSearch = useApp((s) => s.settings.placeSearch) !== false;
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [lookup, setLookup] = useState<Lookup | null>(null);
  const abort = useRef<AbortController | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();

  const q = value.trim();
  const fromTree = useMemo(() => (open && tree && q ? matchPlaces(treePlaces(tree), q) : []), [open, tree, q]);
  const current = lookup?.q === q ? lookup : null;
  const items: Item[] = fromTree.map((p) => ({ kind: 'tree', label: p.place }));
  if (current?.status === 'done') items.push(...current.found.filter((f) => !fromTree.some((p) => p.place === f.label)).map((f) => ({ ...f, kind: 'found' as const })));
  else if (canSearch && q.length >= 2 && current?.status !== 'loading') items.push({ kind: 'search' });
  const firstFound = items.findIndex((i) => i.kind === 'found');
  const shown = open && (items.length > 0 || !!current);
  const place = useDropdownPlace(inputRef, shown, `${items.length}:${current?.status}`, 280);

  useEffect(() => () => abort.current?.abort(), []);

  const close = () => {
    setOpen(false);
    setActive(-1);
  };
  const search = async () => {
    abort.current?.abort();
    const ctl = new AbortController();
    abort.current = ctl;
    setLookup({ q, status: 'loading' });
    try {
      const found = await searchPlaces(q, ctl.signal);
      if (ctl.signal.aborted) return;
      setLookup({ q, status: 'done', found });
      // Keep the keyboard where the results are.
      setActive(found.length ? fromTree.length : -1);
    } catch {
      if (!ctl.signal.aborted) setLookup({ q, status: 'error' });
    }
  };
  const choose = (i: number) => {
    const item = items[i];
    if (!item) return;
    if (item.kind === 'search') return void search();
    onChange(item.label);
    close();
    onPick?.(item.label);
    // After onPick, which may save the field: this saves on top of that, from the latest tree.
    if (item.kind === 'found') {
      const t = getState().tree;
      if (t) commit(withLocations(t, [[item.label, item.location]]));
    }
  };
  const keys = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.nativeEvent.isComposing) return;
    if (e.key === 'ArrowDown' && (items.length || !open)) {
      e.preventDefault();
      if (!open) setOpen(true);
      else setActive((a) => Math.min(items.length - 1, a + 1));
      return;
    }
    if (e.key === 'ArrowUp' && shown) {
      e.preventDefault();
      setActive((a) => Math.max(-1, a - 1));
      return;
    }
    if (e.key === 'Enter' && shown && active >= 0) {
      e.preventDefault();
      e.stopPropagation();
      choose(active);
      return;
    }
    if (e.key === 'Escape' && shown) {
      e.stopPropagation();
      close();
      return;
    }
    if (e.key === 'Tab') close();
    onKeyDown?.(e);
  };

  return (
    <>
      <input
        {...rest}
        ref={inputRef}
        value={value}
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={shown}
        aria-controls={shown ? listId : undefined}
        aria-activedescendant={shown && active >= 0 ? `${listId}-${active}` : undefined}
        autoComplete="off"
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
          setActive(-1);
        }}
        onKeyDown={keys}
        onBlur={(e) => {
          close();
          onBlur?.(e);
        }}
      />
      {shown &&
        place &&
        createPortal(
          // Pressing in the list mustn't take focus from the field (that would end a table cell's edit).
          <div
            className="picker-list place-list"
            role="listbox"
            id={listId}
            aria-label="Places"
            style={place}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            // A portal's events still bubble to the field's React parents, such as a table cell.
            onClick={(e) => e.stopPropagation()}
          >
            {items.map((item, i) => (
              <div key={item.kind === 'search' ? 'search' : `${item.kind}:${item.label}`} style={{ display: 'contents' }}>
                {i === firstFound && <div className="place-heading">OpenStreetMap</div>}
                <div id={`${listId}-${i}`} role="option" aria-selected={i === active} className="picker-item" onClick={() => choose(i)} onMouseEnter={() => setActive(i)}>
                  {item.kind === 'search' ? <Globe size={15} className="place-icon" aria-hidden /> : <MapPin size={15} className="place-icon" aria-hidden />}
                  {item.kind === 'search' ? (
                    <div className="place-search">
                      Search OpenStreetMap for “<b>{q}</b>”
                    </div>
                  ) : (
                    <div style={{ minWidth: 0 }}>
                      <div>{item.label}</div>
                      {item.kind === 'found' && item.type && <div className="meta">{item.type}</div>}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {current?.status === 'loading' && <div className="place-note">Searching OpenStreetMap…</div>}
            {current?.status === 'error' && (
              <div className="place-note">
                Couldn’t reach OpenStreetMap{navigator.onLine ? '' : ' while offline'}.{' '}
                <button type="button" className="link-btn" onClick={search}>
                  Try again
                </button>
              </div>
            )}
            {current?.status === 'done' && !current.found.length && <div className="place-note">OpenStreetMap has nothing called “{q}”. Try adding the state or country.</div>}
            {current?.status === 'done' && current.found.length > 0 && (
              <div className="place-note">
                ©{' '}
                <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">
                  OpenStreetMap contributors
                </a>
              </div>
            )}
          </div>,
          document.body,
        )}
    </>
  );
}
